import React, { useMemo } from 'react';
import { Box, HStack, Modal, VStack, Text, FormControl, WarningIcon } from 'native-base';
import { useGeneralStyles, useMenuStyles, useModalStyles } from './styles';
import { useAcknowledgeNotification, useI18n, useSpringBigStore } from '@springbig/shared';
import { Caption, H3, H4 } from './Typography';
import Button from '~/shared/Button';
import EmailVerificationForm from './EmailVerification/EmailVerificationForm';
import { useOffersScreen } from '@springbig/shared';
import dayjs from 'dayjs';
import MockGiftCard from '~/shared/MockGiftCard';
import { useAppNavigate } from '~/pages';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SpIcon } from '~/shared/Icon';
import { useToast } from '../hooks/toast';

type ClaimGiftCardModalProps = {
    isOpen: boolean;
    close: () => void;
    closeAndCollapse: () => void;
}

const GiftCardInboxModal = ({
    isOpen,
    close,
    closeAndCollapse
}: ClaimGiftCardModalProps) => {

    const { walletId, member, setMember, merchant } = useSpringBigStore();
    const { params, navigate } = useAppNavigate();
    const { card_background } = useMenuStyles();
    const { claimGiftCard } = useOffersScreen();
    const { t } = useI18n();
    const gStyles = useGeneralStyles();
    const {
        text,
        body_background,
    } = useModalStyles();
    const { successToast } = useToast();

    const updateMemberEmail = (validatedEmail: string): void => {

        setMember({
            ...member,
            consumer: {
                ...member.consumer,
                email: validatedEmail,
                email_validated: true
            }
        });
    };

    const giftCardRewardGrants = useMemo(() => {

        return member?.reward_grants.filter((r) => r._type === 'gift_card' && r.active);
    }, [member]);


    const hasUnclaimedGiftCards = useMemo(() => {

        return giftCardRewardGrants?.length > 0;
    }, [member]);

    const unreadGiftCardNotifications = useMemo(() => {

        return member?.active_notifications?.filter((n) => n.message_type === 'gift_card_auto_claimed' && !n.acknowledged);
    }, [member]);

    const hasUnreadGiftCardNotifications = useMemo(() => {

        return unreadGiftCardNotifications?.length > 0;
    }, [member]);

    const goToGiftCards = () => {

        closeAndCollapse();
        navigate(`w/${params.wallet_id}/giftCard`);
    };

    const { mutate: acknowledge } = useAcknowledgeNotification();

    const acknowledgeNotification =  (notificationId: number) => {

        return () => {

            acknowledge({ notification_id: notificationId });

            setMember({
                ...member,
                active_notifications: member?.active_notifications?.filter((n) => n.id !== notificationId)
            });
        };
    };

    const handleClaimGiftCardPressed = (rewardGrantId: number) => {

        return async () => {

            try {
                await claimGiftCard.mutateAsync({ reward_grant_id: rewardGrantId });

                setMember({
                    ...member,
                    reward_grants: member.reward_grants.map((r) => {

                        if (r.id === rewardGrantId) {
                            r.active = false;
                            r.status = 'claimed';
                            r.redemptions_available = 0;
                        }

                        return r;
                    })
                });

                successToast(t('giftCard.claimSuccess', [merchant?.gift_card_alias]));
            }
            catch (e) {
                console.error(e);
            }
        };
    };

    const currentDisplayMode = useMemo(() => {

        if (!member?.consumer?.email_validated) {
            return 'validate-email';
        }

        return 'inbox';
    }, [member]);

    return (
        <Modal isOpen={isOpen} overflowY={'auto'}>
            <Modal.Content>
                <Modal.CloseButton
                    bg={gStyles.button_neutral_background}
                    _icon={{
                        color: gStyles.button_neutral_text,
                    }}
                    onPress={close}
                />
                <Modal.Body
                    bg={body_background}
                >
                    <H3 textAlign='center' color={text} marginBottom={3}>
                        { t('giftCard.inboxHeading', [merchant?.gift_card_alias]) }
                    </H3>
                    { currentDisplayMode === 'validate-email' &&
                        <>
                            <H4 textAlign='center' color={text} marginBottom={3}>
                                { t('giftCard.inboxEmailNotValidatedHeading', [merchant?.gift_card_alias]) }
                            </H4>
                            <EmailVerificationForm
                                t={t}
                                walletId={walletId}
                                onCancel={() => {}}
                                onProceed={updateMemberEmail}
                                emailVerificationFormType='giftCardInbox'
                            />
                        </>
                    }
                    { currentDisplayMode === 'inbox' &&
                        <Box>
                            { hasUnclaimedGiftCards &&
                                <Box marginBottom={4}>
                                    <H4 textAlign='center' color={text} maxWidth='80%' marginX='auto'>
                                        { t('giftCard.inboxGrantsAvailable', [merchant?.gift_card_alias]) }
                                    </H4>
                                    <Caption textAlign='center' color={text} marginBottom={3}>
                                        { t('giftCard.claimBeforeExpiration') }
                                    </Caption>
                                    <VStack space={2} marginBottom={3}>
                                        {claimGiftCard.isError && (
                                            <FormControl isInvalid={claimGiftCard.isError}>
                                                <FormControl.ErrorMessage
                                                    marginBottom={2}
                                                    leftIcon={<WarningIcon size={5} />}
                                                >
                                                    { claimGiftCard.error?.data?.error }
                                                </FormControl.ErrorMessage>
                                            </FormControl>
                                        )}
                                        { giftCardRewardGrants.map((r) => (

                                            <Box bg={card_background} padding='1' rounded='md' key={r.id}>
                                                <HStack space={2} alignItems='center' justifyContent='space-between'>
                                                    <HStack space={2} alignItems='center'>
                                                        <Box width={110}>
                                                            <MockGiftCard size="sm" amount={`$${r.reward.discount_value}`} />
                                                        </Box>
                                                        <Box>
                                                            <H4 color={text}>
                                                                { r.reward.item_name }
                                                            </H4>
                                                            <Caption color={text}>
                                                                { t('giftCard.claimBefore', [dayjs(r.expiration).format('MM/DD/YY')]) }
                                                            </Caption>
                                                        </Box>
                                                    </HStack>
                                                    <Box>
                                                        <Button
                                                            bg={gStyles.button_background}
                                                            _text={{
                                                                color: gStyles.button_text,
                                                            }}
                                                            disabled={claimGiftCard.isLoading}
                                                            onPress={handleClaimGiftCardPressed(r.id)}
                                                        >
                                                            { t('giftCard.claim') }
                                                        </Button>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        )) }
                                    </VStack>
                                    <Text fontSize='xs' textAlign='center' color={text}>
                                        { t('giftCard.claimExtraNote', [merchant?.gift_card_alias]) }
                                    </Text>
                                </Box>
                            }
                            { hasUnreadGiftCardNotifications &&
                                <VStack>
                                    <Box marginBottom={3}>
                                        <H3 color={text} textAlign='center'>
                                            { t('giftCard.inboxGrantsClaimed', [merchant?.gift_card_alias]) }
                                        </H3>
                                        <Caption color={text} textAlign='center'>
                                            { t('giftCard.balanceUpdated', [merchant?.gift_card_alias]) }
                                        </Caption>
                                    </Box>

                                    <VStack space={2} marginBottom={3}>
                                        { unreadGiftCardNotifications.map((n) => (

                                            <Box bg={card_background} padding='1' rounded='md' key={n.id}>
                                                <HStack space={2} alignItems='center' justifyContent='space-between'>
                                                    <HStack space={2} alignItems='center'>
                                                        <Box width={120}>
                                                            <MockGiftCard size="sm" amount={n.meta.value} />
                                                        </Box>
                                                        <Box>
                                                            <H4 color={text}>
                                                                {n.meta.text}
                                                            </H4>
                                                            <Caption color={text}>
                                                                { t('giftCard.addedOn', [dayjs(n.created_at).format('MM/DD/YY')]) }
                                                            </Caption>
                                                        </Box>
                                                    </HStack>
                                                    <Box>
                                                        <Button
                                                            bg={gStyles.button_background}
                                                            _text={{
                                                                color: gStyles.button_text,
                                                            }}
                                                            color={gStyles.button_text}
                                                            onPress={acknowledgeNotification(n.id)}
                                                        >
                                                            <SpIcon icon={faTimes} />
                                                        </Button>
                                                    </Box>
                                                </HStack>
                                            </Box>
                                        )) }
                                    </VStack>
                                </VStack>
                            }
                            <Button
                                bg={gStyles.button_background}
                                _text={{
                                    color: gStyles.button_text,
                                }}
                                onPress={goToGiftCards}
                            >
                                { t('giftCard.navigateTo', [merchant?.gift_card_alias]) }
                            </Button>
                        </Box>
                    }
                </Modal.Body>
            </Modal.Content>
        </Modal>
    );
};

export default GiftCardInboxModal;
